import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import AwardStar from 'components/AwardStar';
import LoadScreen from 'components/LoadScreen';
import RewardCard from './components/RewardCard';
import AudioCaptions from 'components/AudioCaptions';

import { getActivitySequence, resetActivityStore } from 'store/actions/activity';
import { hideAward, setCurrentCard } from 'store/actions/award';
import { getCurrentPosition, updateStudentPosition } from 'store/actions/student';
import { hideForwardButton, loadCommonAssets, setCommonSounds } from 'store/actions/ui';
import AssetLoader from 'utils/assetLoader';

import styles from './RewardScreen.module.scss';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { maybe } from '@lwtears/lwt-common-frontend/lib/@common/util/maybe-util';
import withRouter from '../../hooks/withRouter';
import { t } from '../../translations';
import { compose } from 'redux';

const MaybeAudioCaptions = maybe(AudioCaptions);
export class RewardScreen extends Component {
  constructor(props) {
    super(props);
    this.timers = [];
  }

  handleClick = async card => {
    this.props.setCurrentCard(card.character.character);
    const status = await this.props.getActivitySequence(card.character.character);

    if (status < 400) this.redirectToActivity();
  };

  redirectToActivity = () => {
    const {
      props: {
        activityData: { activities },
        module,
        router
      }
    } = this;
    const { type } = activities[0];

    router.navigate(`/${module}/activity/${type}`);
  };

  loadSound = async sound => {
    const assetLoader = new AssetLoader();
    assetLoader.queueAudio(sound);

    const cache = await assetLoader.downloadAll();
    return sound && cache.get(sound.src);
  };

  onComplete = async () => {
    this.props.hideAward();
    this.props.setCurrentCard('');
    this.props.updateStudentPosition();
    await this.loadSound(this.props.moduleSound).then(sound => {
      if (sound) {
        const tID = setTimeout(() => sound.play(), 1000);
        this.timers.push(tID);
      }
    });
  };

  async componentDidMount() {
    const {
      haveAward,
      hideForwardButton,
      getCurrentPosition,
      resetActivityStore,
      commonSounds,
      module
    } = this.props;

    document.title = `${module === 'letters' ? t('LettersReward') : t('NumbersReward')}`;

    hideForwardButton();

    await getCurrentPosition()
      .then(() => this.loadSound(this.props.moduleSound))
      .then(sound => {
        if (!haveAward && sound) {
          const tID = setTimeout(() => sound.play(), 500);
          this.timers.push(tID);
        }
      });

    resetActivityStore();

    if (Object.keys(commonSounds).length === 0) {
      this.props.loadCommonAssets();
    }
  }

  componentDidUpdate(prevProps) {
    const { haveAward } = this.props;
    if (prevProps.haveAward !== haveAward && haveAward) {
      document.title = `${t('AwardScreen')}`;
    }
  }

  async componentWillUnmount() {
    const sound = await this.loadSound(this.props.moduleSound);
    sound && sound.stop();
    this.timers.forEach(tID => clearTimeout(tID));
  }

  render() {
    const {
      props: {
        activityData: { level },
        currentPosition,
        showBackgroundOfAward,
        haveAward,
        loading,
        module,
        target,
        lwtStoreState: {
          settings: {
            accessibility: {
              displaySettings: { contentScaling },
              closedCaptionsSettings
            }
          }
        }
      }
    } = this;

    const showAudioCaptions =
      closedCaptionsSettings.showClosedCaptions && closedCaptionsSettings.displayCCControl;

    let starRef,
      classes = [];
    if (haveAward) starRef = React.createRef();
    if (loading && !haveAward) return <LoadScreen />; // AwardStar loads twice without !haveAward
    classes.push(styles.RewardScreen);
    if ('letters' === module) classes.push(styles.Letters);

    return (
      <>
        <div id="reward-screen" className={classes.join(' ')}>
          {currentPosition.length
            ? currentPosition.map((row, idx) => {
                return (
                  <div className={styles.Section} key={`row-${idx}`}>
                    {row.group && (
                      <h1 className={[styles.Title, styles[`Title${contentScaling}`]].join(' ')}>
                        {row.group}
                      </h1>
                    )}
                    {row.characters.length > 0 && (
                      <div className={styles.Row}>
                        {row.characters.map(card => {
                          if (haveAward && card.character.character === target)
                            card.starRef = starRef;
                          return (
                            <RewardCard
                              key={`Card-${card.character.character}`}
                              card={card}
                              handleClick={this.handleClick}
                              contentScaling={contentScaling}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            : null}
        </div>
        {true === haveAward && starRef && (
          <>
            <div
              className={styles.captionsContainer}
              style={{ visibility: showBackgroundOfAward ? 'visible' : 'hidden' }}
            >
              <MaybeAudioCaptions shouldRender={showAudioCaptions} className={styles.captions} />
            </div>
            <AwardStar level={level} onComplete={this.onComplete} target={starRef} />
          </>
        )}
      </>
    );
  }
}

RewardScreen.propTypes = {
  activityData: PropTypes.shape({
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string
      })
    ),
    character: PropTypes.object,
    level: PropTypes.number
  }),
  currentPosition: PropTypes.arrayOf(
    PropTypes.shape({
      character: PropTypes.shape({
        character: PropTypes.string,
        id: PropTypes.number
      }),
      stars: PropTypes.number
    })
  ),
  getActivitySequence: PropTypes.func.isRequired,
  getCurrentPosition: PropTypes.func,
  haveAward: PropTypes.bool,
  showBackgroundOfAward: PropTypes.bool,
  hideAward: PropTypes.func,
  router: PropTypes.object,
  loading: PropTypes.bool,
  module: PropTypes.string,
  setCurrentCard: PropTypes.func,
  target: PropTypes.string,
  updateStudentPosition: PropTypes.func,
  hideForwardButton: PropTypes.func,
  resetActivityStore: PropTypes.func,
  moduleSound: PropTypes.object,
  commonSounds: PropTypes.object,
  loadCommonAssets: PropTypes.func,
  lwtStoreState: PropTypes.shape({
    settings: PropTypes.shape({
      accessibility: PropTypes.shape({
        displaySettings: PropTypes.shape({
          contentScaling: PropTypes.number
        }),
        closedCaptionsSettings: PropTypes.object
      })
    })
  })
};

export const mapStateToProps = state => ({
  activityData: state.activity.data,
  currentPosition: state.student.currentPosition,
  moduleSound: state.student.moduleSound[0],
  haveAward: state.award.showAward,
  showBackgroundOfAward: state.award.showBackgroundOfAward,
  loading: state.ui.loading,
  module: state.ui.module,
  target: state.award.currentCard,
  commonSounds: state.ui.sounds.common
});

const mapDispatchToProps = {
  hideAward,
  setCurrentCard,
  getActivitySequence,
  getCurrentPosition,
  updateStudentPosition,
  hideForwardButton,
  resetActivityStore,
  loadCommonAssets,
  setCommonSounds
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default compose(withRedux, withLwtStore, withRouter)(RewardScreen);
